import { ScrollArea } from "@/components/ui/scroll-area";
import SEO from "@/components/SEO";
import Header from "@/components/Header";

const TermsOfService = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title="Terms of Service - Utah Whiskey Tracker"
        description="Terms of Service for Utah Whiskey Tracker. Read our terms and conditions for using our whiskey tracking and alert services."
        canonical="https://utahwhiskeytracker.com/terms-of-service"
      />
      <Header />
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
          <h1 className="text-3xl font-bold text-whiskey-dark mb-6">Terms of Service</h1>
          
          <ScrollArea className="h-[60vh]">
            <div className="pr-4 space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">1. Acceptance of Terms</h2>
                <p className="mb-3">
                  Welcome to Whiskey Tracker. By accessing or using our website and services, you agree to be bound by these Terms of Service 
                  ("Terms") and our Privacy Policy. If you do not agree to these Terms, please do not use our services.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you and Whiskey Tracker regarding your access to and use of the website 
                  and services.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">2. Description of Services</h2>
                <p>
                  Whiskey Tracker provides a platform for users to track the availability of whiskey products at various retailers. Our services include 
                  whiskey availability alerts, inventory tracking, and related features. We reserve the right to modify, suspend, or discontinue any aspect 
                  of our services at any time without notice.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">3. User Accounts</h2>
                <p className="mb-3">
                  To access certain features of our services, you may be required to create an account. When creating an account, you must provide accurate 
                  and complete information. You are solely responsible for maintaining the confidentiality of your account and password.
                </p>
                <p>
                  You accept responsibility for all activities that occur under your account. If you believe your account security has been compromised, 
                  please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">4. SMS Communications</h2>
                <p className="mb-3">
                  If you provide your mobile phone number during registration or in your account settings, you agree to receive SMS messages from Whiskey Tracker 
                  relating to whiskey availability alerts and account notifications.
                </p>
                <p className="mb-3">
                  Standard message and data rates may apply. Message frequency varies. You can opt out of receiving SMS messages at any time by updating your 
                  preferences in Account Settings or by replying STOP to any of our SMS messages.
                </p>
                <p>
                  By providing your phone number and using our services, you confirm that you are the primary account holder or have permission to use the phone 
                  number provided.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">5. User Conduct</h2>
                <p className="mb-2">You agree not to use our services to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Distribute spam, malware, or other harmful content</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper operation of our services</li>
                  <li>Collect user information without consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">6. Subscription Plans and Payments</h2>
                <p className="mb-3">
                  We offer various subscription plans for enhanced services. By subscribing to a paid plan, you agree to pay all applicable fees. All payments 
                  are non-refundable unless otherwise specified.
                </p>
                <p>
                  We reserve the right to change our subscription fees at any time. Any changes will be communicated to you in advance, and you will have the 
                  opportunity to cancel your subscription before the new rates take effect.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">7. Intellectual Property</h2>
                <p>
                  All content, features, and functionality of our services, including but not limited to text, graphics, logos, icons, and software, are the 
                  exclusive property of Whiskey Tracker and are protected by copyright, trademark, and other intellectual property laws.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">8. Disclaimer of Warranties</h2>
                <p>
                  OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED 
                  TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">9. Limitation of Liability</h2>
                <p>
                  TO THE FULLEST EXTENT PERMITTED BY LAW, WHISKEY TRACKER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
                  DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR IN CONNECTION WITH THE USE OF OUR SERVICES.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">10. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating 
                  the "Last Updated" date. Your continued use of our services after such modifications will constitute your acknowledgment and acceptance of the 
                  modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">11. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend your account and access to our services at our sole discretion, without notice, for conduct that 
                  we believe violates these Terms or is harmful to other users, us, or third parties.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">12. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-whiskey-dark mb-3">13. Contact Information</h2>
                <p>
                  If you have any questions about these Terms, please contact us at support@whiskeytracker.com.
                </p>
              </section>

              <p className="text-sm text-gray-500 mt-6">Last Updated: May 23, 2025</p>
            </div>
          </ScrollArea>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
